import { getCountryCallingCode, isValidPhoneNumber } from 'libphonenumber-js';

import type { Form } from '@/types/Form';
import type { Validation } from '@/types/Validation';

import { isEmpty } from '@/utils/isEmpty/isEmpty';

export type { Form, Validation };

// ─── Simple local validators (pre-existing) ───────────────────────────────────

export const email = (value: string): boolean => {
    const emailRegex =
        /^(?:(?:[^<>()[\]\\.,;:\s@"]+(?:\.[^<>()[\]\\.,;:\s@"]+)*)|(?:".+"))@[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+?$/;
    return emailRegex.test(value);
};

export const phone = (value: string): boolean => {
    const phoneRegex =
        /(?:(?:(\s*\(?([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\)?\s*(?:[.-]\s*)?)([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})/;
    return phoneRegex.test(value);
};

export const required = (value: any): boolean => {
    if (typeof value === 'number') {
        return true;
    }
    return !isEmpty(value);
};

export const validatePassword = (value: string): boolean => required(value) && value.length >= 7;

export const validateInternationalZipCode = (zipCode: string): boolean => /^[a-zA-Z0-9-_]+$/.test(zipCode);

// The account profile stores names in 40-character columns, and the backend truncates anything longer
// without an error, leaving the account name different from the one on file elsewhere (e.g. an address).
export const MAX_NAME_LENGTH = 40;

export const isNameTooLong = (value: string): boolean => value.length > MAX_NAME_LENGTH;

export enum NameField {
    First = 'first',
    Last = 'last',
}

export const validateName = (value: string, field: NameField): string | undefined => {
    if (!required(value)) {
        return field === NameField.First ? 'Enter a first name.' : 'Enter a last name.';
    }
    if (isNameTooLong(value)) {
        return field === NameField.First
            ? 'First name must be 40 characters or fewer.'
            : 'Last name must be 40 characters or fewer.';
    }
    return undefined;
};

// ─── Caterwaul-compat validators (inlined from caterwaul) ─────────────────────

const EMAIL_LOCAL_ALLOWED_CHARS = new Set(
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789-!#$%&'*+/=?^_`{|}~"
);

const isEmailLocalValid = (local: string): boolean => {
    if (!local || local[0] === '.' || local[local.length - 1] === '.') {
        return false;
    }
    let prevWasDot = false;
    for (const char of local) {
        if (char === '.') {
            if (prevWasDot) {
                return false;
            }
            prevWasDot = true;
        } else {
            if (!EMAIL_LOCAL_ALLOWED_CHARS.has(char)) {
                return false;
            }
            prevWasDot = false;
        }
    }
    return true;
};

const isAlphaNumeric = (char: string): boolean => {
    const code = char.charCodeAt(0);
    return (code >= 48 && code <= 57) || (code >= 65 && code <= 90) || (code >= 97 && code <= 122);
};

const isAlpha = (char: string): boolean => {
    const code = char.charCodeAt(0);
    return (code >= 65 && code <= 90) || (code >= 97 && code <= 122);
};

const isEmailDomainValid = (domain: string): boolean => {
    if (!domain) {
        return false;
    }
    const labels = domain.split('.');
    if (labels.length < 2) {
        return false;
    }
    for (const label of labels) {
        if (!label || label[0] === '-' || label[label.length - 1] === '-' || label.length > 63) {
            return false;
        }
        for (const char of label) {
            if (!isAlphaNumeric(char) && char !== '-') {
                return false;
            }
        }
    }
    const tld = labels[labels.length - 1];
    return isAlpha(tld[0]) && tld.length >= 2;
};

export const validEmail = (emailValue: string): boolean => {
    if (!emailValue) {
        return true;
    }
    if (emailValue.length > 254) {
        return false;
    }
    const atIndex = emailValue.indexOf('@');
    if (atIndex === -1 || atIndex !== emailValue.lastIndexOf('@')) {
        return false;
    }
    const local = emailValue.slice(0, atIndex);
    const domain = emailValue.slice(atIndex + 1);
    if (local.length > 64) {
        return false;
    }
    return isEmailLocalValid(local) && isEmailDomainValid(domain);
};

const hasAtLeastSevenDigits = (value: string): boolean => {
    let count = 0;
    for (let i = 0; i < value.length; i++) {
        const code = value.charCodeAt(i);
        if (code >= 48 && code <= 57) {
            count++;
            if (count >= 7) {
                return true;
            }
        }
    }
    return false;
};

export const validPhone = (value: string, countryCode?: string, phoneRequired?: boolean): boolean => {
    if (!value && !phoneRequired) {
        return true;
    }
    if (!countryCode) {
        return hasAtLeastSevenDigits(value);
    }
    const ccNumber = getCountryCallingCode(countryCode as any);
    const checkValue = `${ccNumber}${value}`;
    return isValidPhoneNumber(checkValue, countryCode as any);
};

export const validateValidationObject = <F extends Form, V extends Validation<any>>(form: F, validation: V): V => {
    Object.keys(validation).forEach((key) => {
        const field = validation[key as keyof V];
        if (field?.validate) {
            field.errorMessage = field.validate(form[key as keyof F], form);
        }
    });
    return validation;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isValidValidationObject = <V extends Validation<any>>(validation: V): boolean => {
    let valid = true;
    Object.keys(validation).forEach((key) => {
        if ((validation[key as keyof V] as { errorMessage?: string }).errorMessage) {
            valid = false;
        }
    });
    return valid;
};
